import React, { useState, useEffect, useContext } from 'react';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import './Counselling.css';

const Counselling = () => {
    const { user } = useContext(MyContext);
    const [currentView, setCurrentView] = useState('groups'); // groups, counsellors, chat, sessions
    const [userGroups, setUserGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [counsellors, setCounsellors] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [viewerRole, setViewerRole] = useState(null);

    const isCounsellorChatView = viewerRole === 'counsellor';

    // Fetch user's groups with counselling enabled
    const fetchUserGroups = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_user_groups.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: user.user_id })
            });
            const data = await response.json();
            
            if (data.success) {
                // Filter groups that have counselling enabled (you can add this field to groups table)
                setUserGroups(data.data.groups || []);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to fetch groups');
        } finally {
            setLoading(false);
        }
    };

    // Fetch counsellors for a group
    const fetchGroupCounsellors = async (group_id) => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_group_counsellors.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    group_id: group_id, 
                    user_id: user.user_id 
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setCounsellors(data.data.counsellors || []);
                setCurrentView('counsellors');
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to fetch counsellors');
        } finally {
            setLoading(false);
        }
    };

    // Start a counselling conversation
    const startConversation = async (counsellor_id) => {
        try {
            setLoading(true);
            
            // Check if conversation already exists
            const existingSession = conversations.find(session => 
                session.other_participant.id === counsellor_id
            );
            
            if (existingSession) {
                // Load existing conversation
                await loadConversation(existingSession.session_id);
                return;
            }
            
            // Start new conversation
            const response = await fetch(`${backendUrl}/start_counselling_conversation.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    group_id: selectedGroup.id, 
                    member_id: user.user_id,
                    counsellor_id: counsellor_id
                })
            });
            const data = await response.json();
            
            if (data.success) {
                // Load the conversation
                await loadConversation(data.data.conversation_id);
                // Refresh sessions list
                await fetchSessions();
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to start conversation');
        } finally {
            setLoading(false);
        }
    };

    // Load a conversation
    const loadConversation = async (conversation_id) => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_counselling_messages.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    conversation_id: conversation_id, 
                    user_id: user.user_id 
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setSelectedConversation(data.data.conversation);
                setViewerRole(data.data.conversation?.viewer_role || null);
                setMessages(data.data.messages || []);
                setCurrentView('chat');
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to load conversation');
        } finally {
            setLoading(false);
        }
    };

    // Send a message
    const sendMessage = async () => {
        if (!newMessage.trim() || !selectedConversation) return;

        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/send_counselling_message.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    conversation_id: Number(selectedConversation.id),
                    sender_id: Number(user.user_id),
                    message_text: newMessage.trim(),
                    message_type: 'text',
                    cloudinary_url: '',
                    file_name: '',
                    file_size: 0,
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setNewMessage('');
                // Reload messages
                await loadConversation(selectedConversation.id);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to send message');
        } finally {
            setLoading(false);
        }
    };

    // Fetch user's counselling sessions
    const fetchSessions = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_my_counselling_sessions.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: user.user_id })
            });
            const data = await response.json();
            
            if (data.success) {
                setConversations(data.data.sessions || []);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to fetch sessions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            fetchUserGroups();
            fetchSessions();
        }
    }, [user]);

    const renderGroupsView = () => (
        <div className="counselling-groups">
            <div className="counselling-header">
                <h2>📞 Counselling</h2>
                <p>Get free counselling support from your group counsellors</p>
                <div className="counselling-actions">
                    <button 
                        className="sessions-btn"
                        onClick={() => setCurrentView('sessions')}
                    >
                        My Counselling Sessions ({conversations.length})
                    </button>
                    <button 
                        className="counsellor-dashboard-btn"
                        onClick={() => window.location.href = '/counsellor-dashboard'}
                    >
                        👨‍⚕️ Counsellor Dashboard
                    </button>
                </div>
            </div>
            
            <div className="groups-list">
                {userGroups.map(group => (
                    <div key={group.id} className="group-card" onClick={() => {
                        setSelectedGroup(group);
                        fetchGroupCounsellors(group.id);
                    }}>
                        <div className="group-photo">
                            {group.group_photo ? (
                                <img src={group.group_photo} alt={group.name} />
                            ) : (
                                <div className="default-photo">👥</div>
                            )}
                        </div>
                        <div className="group-info">
                            <h3>{group.name}</h3>
                            <div dangerouslySetInnerHTML={{ __html: group.description }} />
                            <span className="group-type">{group.group_type}</span>
                        </div>
                        <div className="group-arrow">→</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderCounsellorsView = () => (
        <div className="counselling-counsellors">
            <div className="counselling-header">
                <button className="back-btn" onClick={() => setCurrentView('groups')}>← Back</button>
                <h2>Counsellors in {selectedGroup?.name}</h2>
                <p>Choose a counsellor to start a conversation or continue an existing one</p>
                <p className="small text-muted">Chat anonymously: your counsellor sees your messages, not your name.</p>
            </div>
            
            <div className="counsellors-list">
                {(() => {
                    const listable = counsellors.filter(
                        (row) => row?.counsellor?.id != null && String(row.counsellor.id) !== String(user.user_id)
                    );
                    if (listable.length === 0) {
                        return (
                            <div className="no-counsellors-msg text-muted p-3">
                                <p className="mb-2">
                                    {counsellors.length === 0
                                        ? 'No counsellors have been assigned to this group yet.'
                                        : 'You are the only counsellor listed for this group, or every counsellor is you — ask an admin to appoint someone else if you need support.'}
                                </p>
                            </div>
                        );
                    }
                    return listable.map((counsellor) => {
                    const existingSession = conversations.find(session => 
                        session.other_participant.id === counsellor.counsellor.id
                    );
                    
                    return (
                        <div key={counsellor.counsellor.id} className="counsellor-card" onClick={() => startConversation(counsellor.counsellor.id)}>
                            <div className="counsellor-photo">
                                {counsellor.counsellor.photo ? (
                                    <img src={counsellor.counsellor.photo} alt={counsellor.counsellor.name} />
                                ) : (
                                    <div className="default-photo">👨‍⚕️</div>
                                )}
                            </div>
                            <div className="counsellor-info">
                                <h3>{counsellor.counsellor.name}</h3>
                                <div className="counsellor-stats">
                                    <span className={`availability ${counsellor.counsellor.is_available ? 'online' : 'offline'}`}>
                                        {counsellor.counsellor.is_available ? '🟢 Available' : '🔴 Busy'}
                                    </span>
                                    <span className="conversations">{Number(counsellor.counsellor.active_conversations ?? 0)} active conversations</span>
                                </div>
                                {existingSession && (
                                    <div className="existing-session">
                                        <span className="session-status">
                                            {existingSession.unread_count > 0 ? 
                                                `📩 ${existingSession.unread_count} unread messages` : 
                                                '💬 Continue conversation'
                                            }
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="counsellor-arrow">
                                {existingSession ? '↩️' : '💬'}
                            </div>
                        </div>
                    );
                });
                })()}
            </div>
        </div>
    );

    const renderChatView = () => (
        <div className="counselling-chat">
            <div className="chat-header">
                <button className="back-btn" onClick={() => { setViewerRole(null); setCurrentView('counsellors'); }}>← Back</button>
                <div className="chat-participant">
                    <div className="participant-photo">
                        {!isCounsellorChatView && selectedConversation?.counsellor?.photo ? (
                            <img src={selectedConversation.counsellor.photo} alt={selectedConversation.counsellor.name} />
                        ) : (
                            <div className="default-photo">{isCounsellorChatView ? '🕵️' : '👨‍⚕️'}</div>
                        )}
                    </div>
                    <div className="participant-info">
                        <h3>{isCounsellorChatView ? 'Anonymous member' : (selectedConversation?.counsellor?.name || 'Counsellor')}</h3>
                        <p>{selectedGroup?.name}</p>
                        <p className="counselling-chat-privacy small text-muted mb-0">
                            {isCounsellorChatView
                                ? 'You do not see this person\'s name. Replies remain confidential.'
                                : 'Your name is hidden from the counsellor.'}
                        </p>
                    </div>
                </div>
            </div>
            
            <div className="chat-messages">
                {messages.map(message => (
                    <div key={message.id} className={`message ${message.sender && message.sender.id != null && String(message.sender.id) === String(user.user_id) ? 'sent' : 'received'}`}>
                        <div className="message-content">
                            <p>{message.message_text}</p>
                            <span className="message-time">
                                {(() => {
                                    // Helper function to safely parse dates for Safari compatibility
                                    const parseDate = (dateString) => {
                                        if (!dateString) return null;
                                        
                                        // If it's already a Date object, return it
                                        if (dateString instanceof Date) return dateString;
                                        
                                        // Manual parsing for "YYYY-MM-DD HH:MM:SS" format
                                        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
                                        if (match) {
                                            const [, year, month, day, hour, minute, second] = match;
                                            // Create date as local time (not UTC)
                                            const date = new Date(
                                                parseInt(year, 10),
                                                parseInt(month, 10) - 1, // Month is 0-indexed
                                                parseInt(day, 10),
                                                parseInt(hour, 10),
                                                parseInt(minute, 10),
                                                parseInt(second, 10)
                                            );
                                            
                                            return isNaN(date.getTime()) ? null : date;
                                        }
                                        
                                        // Fallback to regular Date parsing
                                        try {
                                            const date = new Date(dateString);
                                            return isNaN(date.getTime()) ? null : date;
                                        } catch (error) {
                                            console.error('Error parsing date:', dateString, error);
                                            return null;
                                        }
                                    };
                                    
                                    const date = parseDate(message.created_at);
                                    return date ? date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'Invalid date';
                                })()}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
            
            <div className="chat-input">
                <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    disabled={loading}
                />
                <button onClick={sendMessage} disabled={loading || !newMessage.trim()}>
                    {loading ? '⏳' : '📤'}
                </button>
            </div>
        </div>
    );

    const renderSessionsView = () => (
        <div className="counselling-sessions">
            <div className="counselling-header">
                <button className="back-btn" onClick={() => setCurrentView('groups')}>← Back</button>
                <h2>My Counselling Sessions</h2>
                <p>Continue your counselling conversations</p>
            </div>
            
            <div className="sessions-list">
                {conversations.length > 0 ? (
                    conversations.map(session => (
                        <div key={session.session_id} className="session-card" onClick={() => loadConversation(session.session_id)}>
                            <div className="session-participant">
                                <div className="participant-photo">
                                    {session.other_participant.photo ? (
                                        <img src={session.other_participant.photo} alt={session.other_participant.name} />
                                    ) : (
                                        <div className="default-photo">👨‍⚕️</div>
                                    )}
                                </div>
                                <div className="participant-info">
                                    <h3>{session.other_participant.name}</h3>
                                    <p>{session.group.name}</p>
                                    <span className="user-role">
                                        {session.user_role === 'member' ? '👤 You are the member' : '👨‍⚕️ You are the counsellor'}
                                    </span>
                                </div>
                            </div>
                            <div className="session-details">
                                {session.last_message && (
                                    <p className="last-message">{session.last_message.text}</p>
                                )}
                                <div className="session-meta">
                                    <span className="session-time">
                                        {(() => {
                                            // Helper function to safely parse dates for Safari compatibility
                                            const parseDate = (dateString) => {
                                                if (!dateString) return null;
                                                
                                                // If it's already a Date object, return it
                                                if (dateString instanceof Date) return dateString;
                                                
                                                // Manual parsing for "YYYY-MM-DD HH:MM:SS" format
                                                const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
                                                if (match) {
                                                    const [, year, month, day, hour, minute, second] = match;
                                                    // Create date as local time (not UTC)
                                                    const date = new Date(
                                                        parseInt(year, 10),
                                                        parseInt(month, 10) - 1, // Month is 0-indexed
                                                        parseInt(day, 10),
                                                        parseInt(hour, 10),
                                                        parseInt(minute, 10),
                                                        parseInt(second, 10)
                                                    );
                                                    
                                                    return isNaN(date.getTime()) ? null : date;
                                                }
                                                
                                                // Fallback to regular Date parsing
                                                try {
                                                    const date = new Date(dateString);
                                                    return isNaN(date.getTime()) ? null : date;
                                                } catch (error) {
                                                    console.error('Error parsing date:', dateString, error);
                                                    return null;
                                                }
                                            };
                                            
                                            const dateToUse = session.last_message_at || session.created_at;
                                            const date = parseDate(dateToUse);
                                            return date ? date.toLocaleDateString() : 'Invalid date';
                                        })()}
                                    </span>
                                    {session.unread_count > 0 && (
                                        <span className="unread-badge">{session.unread_count}</span>
                                    )}
                                </div>
                            </div>
                            <div className="session-arrow">💬</div>
                        </div>
                    ))
                ) : (
                    <div className="no-sessions">
                        <p>No counselling sessions yet</p>
                        <p>Start a conversation with a counsellor from your groups</p>
                    </div>
                )}
            </div>
        </div>
    );

    if (loading && currentView === 'groups') {
        return <div className="loading">Loading counselling groups...</div>;
    }

    return (
        <div className="counselling-container">
            {error && <div className="error-message">{error}</div>}
            
            {currentView === 'groups' && renderGroupsView()}
            {currentView === 'counsellors' && renderCounsellorsView()}
            {currentView === 'chat' && renderChatView()}
            {currentView === 'sessions' && renderSessionsView()}
        </div>
    );
};

export default Counselling;
